import { TextButton } from "@rosette/web_components/src/ui/TextButton.js";
import { Button } from "@rosette/web_components/src/ui/Button.js";
import { useGameSource } from "@/components/game/GameUIContext.js";
import type { MutableRefObject, ReactNode } from "react";
import React from "react";
import { getRulesetIcon } from "@/components/icon/getRulesetIcon.js";
import { useRune } from "@rosette/react_utils/src/useRune.js";
import Link from "next/link";
import { getGameSettingsReadMorePath } from "@rosette/web_components/src/game/menu/rules/getGameSettingsReadMorePath.js";
import { plural } from "@rosette/ts/src/util/formatting.js";
import { PathType } from "@rosette/ts/src/royalur/model/path/PathType.js";
import {
    MENU_BUTTON, MenuOptions,
} from "@/components/game/menu/MenuLayout.js";
import { InlineIcon } from "@rosette/web_components/src/ui/InlineIcon.js";
import Image from "next/image";
import { Card } from "@rosette/web_components/src/ui/Card.js";


interface RuleLineProps {
    label: string;
    children?: ReactNode;
}

function RuleLine({ label, children }: RuleLineProps) {
    return (
        <p className="mt-3 mb-0">
            <span className="font-bold">
                {label}
            </span>
            {children}
        </p>
    );
}


interface RulesMenuProps {
    contentRef: MutableRefObject<HTMLDivElement | null>;
    back: () => void;
    showPath: () => void;
}


export function RulesMenu({
    contentRef, back, showPath,
}: RulesMenuProps) {
    const gameSource = useGameSource();
    const game = useRune(gameSource.game);
    const settings = gameSource.getKnownSetupSettings();

    let settingsDesc;
    if (settings) {
        const rules = game.getRules().getSettings();
        const pathName = PathType.getByID(rules.getPaths().getID()).getName();
        const startingPieces = rules.getStartingPieceCount();
        const safeRosettes = rules.areRosettesSafe();
        const extraRosettes = rules.doRosettesGrantExtraRolls();
        const ruleElems: ReactNode[] = [
            (
                <RuleLine key="dice" label="Dice:">
                    {` ${rules.getDice().getName()}`}
                </RuleLine>
            ),
            (
                <RuleLine key="path" label="Path:">
                    {` ${pathName}'${pathName.endsWith("s") ? "" : "s"} Path `}
                    (
                    <TextButton
                        className="inline-block"
                        onClick={showPath}>

                        View Path
                    </TextButton>
                    )
                </RuleLine>
            ),
            (
                <RuleLine key="starting_piece_count" label="Pieces:">
                    {` ${startingPieces} ${plural("Piece", startingPieces)}`}
                </RuleLine>
            ),
            (
                <RuleLine key="rosettes" label="Rosettes:">
                    {" "}
                    {safeRosettes && extraRosettes ? "Safe & Extra Roll"
                        : (safeRosettes ? "Safe & No Extra Roll"
                                : extraRosettes ? "Unsafe & Extra Roll"
                                    : "Unsafe & No Extra Roll")}
                </RuleLine>
            ),
            rules.doCapturesGrantExtraRolls() && (
                <RuleLine key="captures" label="Captures:">
                    {" Extra Roll"}
                </RuleLine>
            ),
        ];

        const rulesType = settings.getRuleset();
        const readMorePath = getGameSettingsReadMorePath(rulesType);
        settingsDesc = (
            <>
                <h1 className="m-0">
                    <InlineIcon size="lg" className="mr-[0.4em]">
                        <Image
                            src={getRulesetIcon(settings.getRuleset())}
                            alt={rulesType.getName()} />
                    </InlineIcon>

                    {`${rulesType.getName()} Rules`}
                </h1>
                {ruleElems}
                <p className="mt-3 mb-0">
                    <Link href={readMorePath.toString()} target="_blank">
                        Read more about the rules here.
                    </Link>
                </p>
            </>
        );
    } else {
        settingsDesc = (
            <p className="m-0">
                The settings of this game are unknown.
            </p>
        );
    }

    return (
        <MenuOptions contentRef={contentRef}>
            <Card className="max-w-[24em] px-6 py-3 md:py-3">
                {settingsDesc}
            </Card>

            <Button
                size="lg"
                className={MENU_BUTTON}
                onClick={back}>
                Back
            </Button>
        </MenuOptions>
    );
}
